import { IPostRepository } from "../../domain/repositories";
import { ICategoryRepository } from "../../domain/repositories/CategoryRepository";
import {
  IGetHeadSeoParams,
  ISeoService,
  ISeoServiceParams,
} from "../../domain/services/SeoService";
import { HeadSeoDto } from "../http/dto/HeadSeoDto";
import { getImageUrl } from "utils";

// TODO: Move to a config
const BASE_URL = process.env.VERCEL_URL
  ? `https://${process.env.VERCEL_URL}`
  : "https://www.forcode.es";
const TITLE_SUFFIX = "[forCode]";
const BASE_TITLE = `Blog de programación - ${TITLE_SUFFIX}`;
const BASE_DESCRIPTION =
  "Blog de programación centrado en compartir soluciones de nuestro día a día en habla hispana.";
const BASE_TAGS =
  "blog, programación, español, tutoriales, lenguajes de programación";
const BASE_DATE = new Date().toISOString();
const BASE_AUTHOR = "[forCode]";
const TWITTER_ACCOUNT = "@forCodeOfficial";
const CLOUDINARY_FOLDER = "forcode.es";
const BASE_IMAGE_URL = `${CLOUDINARY_FOLDER}/generic/forcode_default_kuqkkd.webp`;

export class SeoService implements ISeoService {
  private _categoryRepository: ICategoryRepository;
  private _postRepository: IPostRepository;

  constructor({ categoryRepository, postRepository }: ISeoServiceParams) {
    this._categoryRepository = categoryRepository;
    this._postRepository = postRepository;
  }

  private _getSeoBaseInfo = () => {
    const headSeo = {
      title: BASE_TITLE,
      description: BASE_DESCRIPTION,
      canonical: `${BASE_URL}/`,
      url: BASE_URL,
      twitterAccount: TWITTER_ACCOUNT,
      keywords: BASE_TAGS,
      date: BASE_DATE,
      author: BASE_AUTHOR,
      imageUrl: getImageUrl({ src: BASE_IMAGE_URL }),
    } as HeadSeoDto;
    return headSeo;
  };

  private _getSeoForCategory = async (slug: string) => {
    const category = await this._categoryRepository.getCategoryBySlug(slug);
    const headSeo = this._getSeoBaseInfo();

    headSeo.title = `${category.name} - ${TITLE_SUFFIX}`;
    headSeo.canonical = category.urlSlug;

    return headSeo;
  };

  private _getSeoForPost = async (slug: string) => {
    const post = await this._postRepository.getPostBySlug({
      slug,
      isDraft: false,
    });
    const headSeo = this._getSeoBaseInfo();

    headSeo.title = post?.title
      ? `${post?.title} - ${TITLE_SUFFIX}`
      : BASE_TITLE;
    headSeo.description = post?.excerpt || BASE_DESCRIPTION;
    headSeo.keywords = post
      ? `${BASE_TAGS} ${post?.tags?.join(", ")}`
      : BASE_TAGS;
    headSeo.date = post
      ? new Date(post.meta.createdAt).toISOString()
      : BASE_DATE;
    headSeo.author = post ? post.author.name : BASE_AUTHOR;

    const imageUrl =
      post.image.provider === "cloudinary"
        ? getImageUrl({ src: post.image.src })
        : `${BASE_URL}/${post.image.src}`;

    headSeo.imageUrl = imageUrl;
    headSeo.canonical = post?.url || headSeo.canonical;

    return headSeo;
  };

  execute = async ({ type, slug }: IGetHeadSeoParams) => {
    if ((!type && !slug) || !type || !slug) return this._getSeoBaseInfo();

    switch (type) {
      case "category":
        return this._getSeoForCategory(slug);

      case "post":
        return this._getSeoForPost(slug);

      default:
        return this._getSeoBaseInfo();
    }
  };
}
